/** @jsxImportSource @emotion/react */

'use client';
'use no memo';

import {
  ArrowDownward,
  ArrowUpward,
  ChevronLeft,
  ChevronRight,
  MoreVert,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  type SxProps,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  type Column,
  type ColumnDef,
  type ColumnSizingState,
  type ColumnSort,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  type Header,
  type HeaderGroup,
  type OnChangeFn,
  type Table as ReactTable,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  type CSSProperties,
  Fragment,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { formatNumber, pluralize } from '@campaign-manager/core/lib/utils';
import { useLocalization } from '@campaign-manager/feature/context/Localization';

import Text from '../Text';
import Toolbar from '../Toolbar';
import * as styles from './styles';

interface Props<T> {
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  error?: Error;
  errorState?: ReactNode;
  emptyState?: ReactNode;
  getRowCanExpand?: (row: Row<T>) => boolean;
  expandedRowRender?: (row: Row<T>) => ReactNode;
  defaultExpanded?: boolean;
  expandedRowSpansFull?: boolean;
  afterEachRowRender?: (row: Row<T>) => ReactNode;
  showSorting?: boolean;
  manualSorting?: boolean;
  defaultOrderBy?: ColumnSort;
  onOrderByChange?: (orderBy: ColumnSort | undefined) => void;
  showPagination?: boolean;
  onNextPageClick?: () => void;
  canGetNextPage?: boolean;
  onPrevPageClick?: () => void;
  canGetPrevPage?: boolean;
  showPageSizeSelect?: boolean;
  maxPageSize?: number;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  rowsCount?: number;
  tableRef?: RefObject<HTMLTableElement | null>;
  rowsAlignedTopVertically?: boolean;
  enablePinning?: boolean;
  enableRowPinning?: boolean;
  alwaysLeftPinnedColumns?: string[];
  alwaysTopPinnedRows?: string[];
  alwaysBottomPinnedRows?: string[];
  stickyHeader?: boolean;
  sx?: SxProps;
  getRowId?: (row: T) => string;
  enableColumnResizing?: boolean;
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
}

export default function Table<T>({
  columns,
  data,
  loading,
  error,
  errorState,
  emptyState,
  getRowCanExpand,
  expandedRowRender,
  defaultExpanded,
  expandedRowSpansFull,
  afterEachRowRender,
  showSorting,
  manualSorting,
  defaultOrderBy,
  onOrderByChange,
  showPagination,
  onNextPageClick,
  canGetNextPage,
  onPrevPageClick,
  canGetPrevPage,
  showPageSizeSelect,
  maxPageSize,
  pageSize,
  onPageSizeChange,
  rowsCount,
  tableRef,
  rowsAlignedTopVertically,
  enablePinning,
  enableRowPinning,
  alwaysLeftPinnedColumns,
  alwaysTopPinnedRows,
  alwaysBottomPinnedRows,
  stickyHeader,
  sx,
  getRowId = (row: T) => (row as any).id,
  enableColumnResizing,
  columnSizing,
  onColumnSizingChange,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(defaultOrderBy ? [defaultOrderBy] : []);
  const [expanded, setExpanded] = useState<ExpandedState>(defaultExpanded ? true : {});
  const [internalColumnSizing, setInternalColumnSizing] = useState<ColumnSizingState>({});
  const isColumnSizingControlled = columnSizing !== undefined;
  const effectiveColumnSizing = isColumnSizingControlled ? columnSizing : internalColumnSizing;
  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = onColumnSizingChange
    ? onColumnSizingChange
    : setInternalColumnSizing;
  const [, setResizeRedrawKey] = useState(0);
  const handleResizeEnd = useCallback(() => {
    setResizeRedrawKey((k) => k + 1);
  }, []);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      expanded,
      columnSizing: effectiveColumnSizing,
    },
    getRowId,
    getRowCanExpand,
    manualSorting,
    sortDescFirst: true,
    onSortingChange: setSorting,
    onColumnSizingChange: handleColumnSizingChange,
    enableColumnResizing: !!enableColumnResizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getSubRows: (row) =>
      Object.prototype.hasOwnProperty.call(row, 'subRows') ? (row as { subRows: any }).subRows : [],
    // This default size config is for the size:0 columns that shrink to fit to the content width
    defaultColumn: {
      size: 1,
      minSize: 0,
    },
    meta: {
      rowsAlignedTopVertically,
    },
    enablePinning: enablePinning,
    enableRowPinning: enableRowPinning,
  });

  const { rows } = table.getSortedRowModel();

  const isError = !!error;
  const isEmpty = rows.length === 0;
  const isEdgeState = isError || isEmpty;

  const [maxNumRows, setMaxNumRows] = useState(0);
  useEffect(() => {
    if (rows.length > maxNumRows) {
      setMaxNumRows(rows.length);
    }
  }, [rows.length, maxNumRows]);

  useEffect(() => {
    if (loading || isError || isEmpty) {
      table.setColumnPinning({});
      return;
    }

    table.setColumnPinning({
      left: alwaysLeftPinnedColumns,
    });
  }, [table, alwaysLeftPinnedColumns, loading, isError, isEmpty]);

  useEffect(() => {
    if (loading || isError || isEmpty) {
      table.setRowPinning({});
      return;
    }

    table.setRowPinning({
      top: alwaysTopPinnedRows,
      bottom: alwaysBottomPinnedRows,
    });
  }, [table, alwaysTopPinnedRows, alwaysBottomPinnedRows, loading, isError, isEmpty]);

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  return (
    <TableWrapper sx={sx}>
      <TableComponent
        hasLeftPinnedColumns={
          Array.isArray(alwaysLeftPinnedColumns) && alwaysLeftPinnedColumns.length > 0
        }
        loading={loading}
        isEdgeState={isEdgeState}
        tableRef={tableRef}
      >
        <TableHead
          dataRef={dataRef}
          table={table}
          heads={table.getHeaderGroups()}
          showSorting={showSorting}
          onOrderByChange={manualSorting ? onOrderByChange : undefined}
          loading={loading}
          isError={isError}
          isEmpty={isEmpty}
          sticky={stickyHeader}
          enableColumnResizing={enableColumnResizing}
          onResizeEnd={enableColumnResizing ? handleResizeEnd : undefined}
        />
        <tbody>
          <TableBody
            dataRef={dataRef}
            table={table}
            cols={table.getAllLeafColumns()}
            rows={rows}
            expandedRowRender={expandedRowRender}
            expandedRowSpansFull={expandedRowSpansFull}
            afterEachRowRender={afterEachRowRender}
            maxNumRows={maxNumRows}
            loading={loading}
            isError={isError}
            errorState={errorState}
            isEmpty={isEmpty}
            emptyState={emptyState}
            topPinnedRowIds={alwaysTopPinnedRows}
            bottomPinnedRowIds={alwaysBottomPinnedRows}
          />
        </tbody>
      </TableComponent>
      <TableFooter
        showPagination={showPagination}
        onNextPageClick={onNextPageClick}
        canGetNextPage={canGetNextPage}
        onPrevPageClick={onPrevPageClick}
        canGetPrevPage={canGetPrevPage}
        showPageSizeSelect={showPageSizeSelect}
        maxPageSize={maxPageSize}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        rowsCount={rowsCount}
      />
    </TableWrapper>
  );
}

interface TableWrapperProps {
  children: ReactNode;
  sx?: SxProps;
}

export function TableWrapper({ children, sx }: TableWrapperProps) {
  return (
    <Box
      sx={sx}
      css={styles.tableContainerWrapper}
    >
      {children}
    </Box>
  );
}

function mergeRefs<T>(...inputRefs: Array<Ref<T> | undefined>) {
  return (ref: T | null) => {
    inputRefs.forEach((inputRef) => {
      if (!inputRef) {
        return;
      }

      if (typeof inputRef === 'function') {
        inputRef(ref);
      } else {
        // eslint-disable-next-line no-param-reassign
        inputRef.current = ref;
      }
    });
  };
}

interface TableComponentProps {
  children: ReactNode;
  loading?: boolean;
  isEdgeState?: boolean;
  tableRef?: RefObject<HTMLTableElement | null>;
  hasLeftPinnedColumns?: boolean;
}

export function TableComponent({
  children,
  loading,
  isEdgeState,
  tableRef,
  hasLeftPinnedColumns,
  ...props
}: TableComponentProps) {
  const leftBoundary = useRef<HTMLElement | null>(null);
  const rightBoundary = useRef<HTMLElement | null>(null);
  const el = useRef<HTMLElement | null>(null);

  const elWidth = el.current ? el.current.clientWidth : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    showOutsideBoundaries(el, leftBoundary, rightBoundary);
  }, [elWidth]);

  return (
    <Box
      css={styles.tableContainer}
      onScroll={() => {
        showOutsideBoundaries(el, leftBoundary, rightBoundary);
      }}
      {...props}
    >
      <Stack
        direction="row"
        sx={{
          flexWrap: 'nowrap',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          minWidth: 'fit-content',
          flex: '1 1 auto',
        }}
      >
        {!hasLeftPinnedColumns && (
          <Box
            ref={leftBoundary}
            css={styles.leftBoundary}
          />
        )}
        <table
          ref={mergeRefs(el as MutableRefObject<HTMLTableElement>, tableRef)}
          cellSpacing={0}
          cellPadding={0}
          css={[styles.table, !loading && isEdgeState && styles.tableEdgeState]}
        >
          {children}
        </table>
        <Box
          ref={rightBoundary}
          css={styles.rightBoundary}
        />
      </Stack>
    </Box>
  );
}

function showOutsideBoundaries(
  el: MutableRefObject<HTMLElement | null>,
  leftBoundary: MutableRefObject<HTMLElement | null>,
  rightBoundary: MutableRefObject<HTMLElement | null>,
) {
  if (el.current === null) {
    return;
  }

  const elRectList = el.current.getClientRects();

  if (leftBoundary.current !== null) {
    const leftBoundaryRectList = leftBoundary.current.getClientRects();
    if (
      elRectList.length > 0 &&
      leftBoundaryRectList.length > 0 &&
      Math.floor(elRectList[0].left) < Math.floor(leftBoundaryRectList[0].right)
    ) {
      leftBoundary.current.style.opacity = '1';
    } else {
      leftBoundary.current.style.opacity = '0';
    }
  }

  if (rightBoundary.current !== null) {
    const rightBoundaryRectList = rightBoundary.current.getClientRects();
    if (
      elRectList.length > 0 &&
      rightBoundaryRectList.length > 0 &&
      Math.floor(elRectList[0].right) > Math.floor(rightBoundaryRectList[0].left)
    ) {
      rightBoundary.current.style.opacity = '1';
    } else {
      rightBoundary.current.style.opacity = '0';
    }
  }
}

interface SelectionToolbarConfig {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isAllSelected: boolean;
  isSomeSelected: boolean;
  actions?: Array<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
    divider?: boolean;
  }>;
}

// Size deltas below this are treated as noise by the resize-redraw guards.
// Both guards must share it: divergent thresholds between the header and row
// observers would let one keep reacting to oscillations the other suppresses.
const SUBPIXEL_EPSILON = 0.5;

// Force a re-render when the observed element resizes so pinned-column offsets
// in `getCommonPinningStyles` (which read DOM widths during render) recompute.
// Critical: only bump on *real* size changes — `getCommonPinningStyles` applies
// inline styles based on measured widths, which can shift layout by a
// sub-pixel and feed back into this observer. Comparing against the last
// observed size and ignoring sub-pixel deltas breaks that loop. Firefox in
// particular keeps oscillating on fractional layout deltas that Chromium
// settles, so an unguarded observer livelocks the tab there.
// Known tradeoff: the guard also swallows a *real* interior redistribution
// whose net effect on the observed box stays sub-pixel (one pinned column
// grows while siblings shrink), leaving pinned offsets stale until the next
// super-threshold change. That knife-edge is accepted as the price of not
// livelocking.
function useGuardedResizeRedraw(ref: RefObject<Element | null>, enabled: boolean) {
  const lastSizeRef = useRef<{ w: number; h: number } | null>(null);
  const [, setRedrawKey] = useState(0);
  useEffect(() => {
    if (!enabled) {
      // Drop the remembered size: after a re-enable the observer's initial
      // callback must not be swallowed by a pre-disable measurement, or the
      // one redraw that adapts to the newly-pinned layout never fires.
      lastSizeRef.current = null;
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      const last = lastSizeRef.current;
      if (
        last &&
        Math.abs(last.w - width) < SUBPIXEL_EPSILON &&
        Math.abs(last.h - height) < SUBPIXEL_EPSILON
      ) {
        return;
      }
      lastSizeRef.current = { w: width, h: height };
      setRedrawKey((k) => k + 1);
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, enabled]);
}

// The redraw exists only so LEFT-pinned column offsets recompute —
// `getCommonPinningStyles` reads sibling DOM widths solely on its
// `isPinned === 'left'` branch. Without left-pinned columns the computed
// styles are layout-independent, so observing is pure overhead.
function useRowResizeRedraw<T>(ref: RefObject<Element | null>, table: ReactTable<T>) {
  useGuardedResizeRedraw(ref, table.getIsSomeColumnsPinned('left'));
}

interface TableHeadProps<T> {
  dataRef: MutableRefObject<T[]>;
  table: ReactTable<T>;
  heads: HeaderGroup<T>[];
  showSorting?: boolean;
  onOrderByChange?: (orderBy: ColumnSort | undefined) => void;
  loading?: boolean;
  isError: boolean;
  isEmpty: boolean;
  sticky?: boolean;
  selectionToolbar?: SelectionToolbarConfig;
  enableColumnResizing?: boolean;
  onResizeEnd?: () => void;
}

export function TableHead<T>({
  dataRef,
  table,
  heads,
  showSorting,
  onOrderByChange,
  loading,
  isError,
  isEmpty,
  sticky,
  selectionToolbar,
  enableColumnResizing,
  onResizeEnd,
}: TableHeadProps<T>) {
  // Selection toolbar menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleActionClick = (action: () => void) => {
    action();
    handleMenuClose();
  };
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const getCommonPinningStylesCb = useCallback(
    (column: Column<T>) => getCommonPinningStyles(theadRef, table, column),
    [dataRef, table],
  );

  useRowResizeRedraw(theadRef, table);

  const isApplyingStickyStyles = useMemo(
    () => sticky && !loading && !isError && !isEmpty,
    [sticky, loading, isError, isEmpty],
  );

  const totalColumns = heads.reduce((sum, headGroup) => {
    return Math.max(
      sum,
      headGroup.headers.reduce((s, h) => s + h.colSpan, 0),
    );
  }, 0);

  const showSelectionToolbar = selectionToolbar && selectionToolbar.selectedCount > 0;

  return (
    <thead
      ref={theadRef}
      css={styles.thead}
    >
      {/* Selection toolbar row - rendered when items are selected */}
      {showSelectionToolbar && (
        <tr>
          <th
            colSpan={totalColumns}
            css={[styles.th, ...(isApplyingStickyStyles ? [styles.thStickyHead] : [])]}
            style={{ padding: 0 }}
          >
            <Toolbar
              noBackground
              sx={{
                minHeight: 56,
                py: 0,
                px: 2,
                alignItems: 'center',
                borderRadius: 0,
              }}
            >
              {/* Select all checkbox */}
              <Checkbox
                checked={selectionToolbar.isAllSelected}
                indeterminate={selectionToolbar.isSomeSelected && !selectionToolbar.isAllSelected}
                onChange={
                  selectionToolbar.isAllSelected
                    ? selectionToolbar.onDeselectAll
                    : selectionToolbar.onSelectAll
                }
                size="small"
                sx={{ py: 0 }}
              />

              {/* Selected count */}
              <Text size="sm">
                {selectionToolbar.selectedCount} selected
                {selectionToolbar.totalCount > 0 && ` of ${selectionToolbar.totalCount}`}
              </Text>

              {/* Select all button */}
              {!selectionToolbar.isAllSelected && (
                <Typography
                  component="button"
                  onClick={selectionToolbar.onSelectAll}
                  sx={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: grey[600],
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Select all
                </Typography>
              )}

              {/* Clear selection button */}
              <Typography
                component="button"
                onClick={selectionToolbar.onDeselectAll}
                sx={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: grey[600],
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear selection
              </Typography>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Actions menu */}
              {selectionToolbar.actions && selectionToolbar.actions.length > 0 && (
                <>
                  <IconButton
                    onClick={handleMenuClick}
                    size="small"
                    aria-label="More actions"
                    aria-controls={menuOpen ? 'selection-actions-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={menuOpen ? 'true' : undefined}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    id="selection-actions-menu"
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    {selectionToolbar.actions.flatMap((action, index) => {
                      const items: ReactNode[] = [];

                      if (action.divider && index > 0) {
                        items.push(<Divider key={`divider-${index}`} />);
                      }

                      items.push(
                        <MenuItem
                          key={`item-${index}`}
                          onClick={() => handleActionClick(action.onClick)}
                          disabled={action.disabled}
                        >
                          {action.label}
                        </MenuItem>,
                      );

                      return items;
                    })}
                  </Menu>
                </>
              )}
            </Toolbar>
            <div css={[...(isApplyingStickyStyles ? [styles.thStickyHeadShadow] : [])]} />
          </th>
        </tr>
      )}
      {/* Normal header row - always rendered for column width calculation, hidden when selection toolbar is shown */}
      {heads.map((headGroup) => (
        <tr
          key={headGroup.id}
          style={showSelectionToolbar ? { visibility: 'collapse' } : undefined}
        >
          {headGroup.headers.map((head) => (
            <th
              key={head.id}
              colSpan={head.colSpan}
              data-col-id={head.column.id}
              css={[
                styles.th,
                ...(!showSelectionToolbar && isApplyingStickyStyles ? [styles.thStickyHead] : []),
                ...getPinnedColumnThClasses(head.column),
              ]}
              style={{
                ...getCommonPinningStylesCb(head.column),
                ...(isGrowColumn(head.column.columnDef) ? { width: '100%' } : {}),
              }}
            >
              <div css={styles.head}>
                <div
                  css={[
                    styles.headTitleContainer,
                    isRightAligned(head.column.columnDef) && styles.headTitleContainerRight,
                  ]}
                >
                  {isRightAligned(head.column.columnDef) &&
                    showSorting &&
                    head.column.getCanSort() && (
                      <SortingButton
                        head={head}
                        onOrderByChange={onOrderByChange}
                      />
                    )}
                  <div css={styles.headTitle}>
                    {head.isPlaceholder
                      ? null
                      : flexRender(head.column.columnDef.header, head.getContext())}
                  </div>
                  {!isRightAligned(head.column.columnDef) &&
                    showSorting &&
                    head.column.getCanSort() && (
                      <SortingButton
                        head={head}
                        onOrderByChange={onOrderByChange}
                      />
                    )}
                </div>
              </div>
              <div css={[...(isApplyingStickyStyles ? [styles.thStickyHeadShadow] : [])]} />
              <div css={[...(head.column.getIsPinned() ? [styles.thLeftPinnedShadow] : [])]} />
              {enableColumnResizing &&
                head.column.getCanResize() &&
                !isGrowColumn(head.column.columnDef) && (
                  <ResizeHandle
                    header={head}
                    table={table}
                    onResizeEnd={onResizeEnd}
                  />
                )}
            </th>
          ))}
        </tr>
      ))}
    </thead>
  );
}

interface ResizeHandleProps<T> {
  header: Header<T, unknown>;
  table: ReactTable<T>;
  onResizeEnd?: () => void;
}

interface ResizeHint {
  edgeX: number;
  hintX: number;
  hintY: number;
  tableTop: number;
  tableBottom: number;
  width: number;
}

function ResizeHandle<T>({ header, table, onResizeEnd }: ResizeHandleProps<T>) {
  const [hint, setHint] = useState<ResizeHint | null>(null);
  const isResizing = hint !== null;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const handle = e.currentTarget;
      const th = handle.closest('th') as HTMLTableCellElement | null;
      if (!th) return;
      const tableEl = th.closest('table') as HTMLTableElement | null;
      if (!tableEl) return;

      const startX = e.clientX;
      const thRect = th.getBoundingClientRect();
      const startWidth = thRect.width;
      const columnLeft = thRect.left;
      const minWidth = Math.max(50, header.column.columnDef.minSize ?? 0);
      const maxWidth = header.column.columnDef.maxSize ?? Number.POSITIVE_INFINITY;
      const columnId = header.column.id;

      // If the resized column is left-pinned, the `left` offset of every
      // subsequent left-pinned column must shift in lockstep with the width
      // change. Capture each one's currently-rendered left so we can write
      // the new value into its CSS variable each frame.
      const widthVar = `--col-${columnId}-w`;
      const minWidthVar = `--col-${columnId}-mw`;
      const maxWidthVar = `--col-${columnId}-Mw`;
      const subsequentLeftPinned: Array<{ leftVar: string; initialLeft: number }> = [];
      if (header.column.getIsPinned() === 'left') {
        const visibleCols = table.getVisibleLeafColumns();
        const myIndex = visibleCols.indexOf(header.column);
        const tableLeft = tableEl.getBoundingClientRect().left;
        for (let i = myIndex + 1; i < visibleCols.length; i++) {
          const col = visibleCols[i];
          if (col.getIsPinned() !== 'left') continue;
          const probeCell = tableEl.querySelector<HTMLElement>(
            `[data-col-id="${typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(col.id) : col.id}"]`,
          );
          const initialLeft = probeCell ? probeCell.getBoundingClientRect().left - tableLeft : 0;
          subsequentLeftPinned.push({ leftVar: `--col-${col.id}-l`, initialLeft });
        }
      }

      const previousBodyCursor = document.body.style.cursor;
      const previousBodyUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const initialTableRect = tableEl.getBoundingClientRect();
      setHint({
        edgeX: columnLeft + startWidth,
        hintX: e.clientX,
        hintY: e.clientY,
        tableTop: initialTableRect.top,
        tableBottom: initialTableRect.bottom,
        width: Math.round(startWidth),
      });

      let rafId: number | null = null;
      let lastEvent: PointerEvent | null = null;
      let lastNext = startWidth;

      const flush = () => {
        rafId = null;
        const event = lastEvent;
        if (!event) return;
        const delta = event.clientX - startX;
        const next = Math.min(maxWidth, Math.max(minWidth, startWidth + delta));
        lastNext = next;

        // Direct DOM writes — no React render. Cells reference these vars in
        // their inline style, so the browser only does a style/layout pass.
        const px = `${next}px`;
        tableEl.style.setProperty(widthVar, px);
        tableEl.style.setProperty(minWidthVar, px);
        tableEl.style.setProperty(maxWidthVar, px);
        if (subsequentLeftPinned.length > 0) {
          const widthDelta = next - startWidth;
          for (const { leftVar, initialLeft } of subsequentLeftPinned) {
            tableEl.style.setProperty(leftVar, `${initialLeft + widthDelta}px`);
          }
        }

        // The hint update is local to ResizeHandle and doesn't propagate to
        // the table tree, so its render is cheap.
        const tableRect = tableEl.getBoundingClientRect();
        setHint({
          edgeX: columnLeft + next,
          hintX: event.clientX,
          hintY: event.clientY,
          tableTop: tableRect.top,
          tableBottom: tableRect.bottom,
          width: Math.round(next),
        });
      };

      const onMove = (event: PointerEvent) => {
        lastEvent = event;
        if (rafId !== null) return;
        rafId = requestAnimationFrame(flush);
      };

      const cleanup = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', cleanup);
        window.removeEventListener('pointercancel', cleanup);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
          // Apply the most recent cursor position synchronously — without this,
          // a brief drag where pointerup fires before the next rAF leaves
          // `lastNext` (and the CSS variable) at the previous frame's value.
          if (lastEvent) flush();
        }
        document.body.style.cursor = previousBodyCursor;
        document.body.style.userSelect = previousBodyUserSelect;

        // Commit the final size to React state so it persists and the
        // CSS-variable fallback (the rendered `${size}px`) catches up.
        table.setColumnSizing((prev) => ({ ...prev, [columnId]: lastNext }));

        setHint(null);
        // Force the entire table to re-render once the drag settles so all
        // absolute-positioning calcs (pinned offsets, boundary shadows, sticky
        // header shadow) are recomputed from the freshly laid-out DOM rather
        // than relying solely on the async ResizeObserver paths.
        if (onResizeEnd) {
          requestAnimationFrame(() => {
            onResizeEnd();
          });
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', cleanup);
      window.addEventListener('pointercancel', cleanup);
    },
    [header.column, table, onResizeEnd],
  );

  const onDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const handle = e.currentTarget;
      const tableEl = handle.closest('table') as HTMLTableElement | null;
      if (tableEl) {
        // Clear the per-column CSS vars so the cell returns to its auto-fit
        // behavior (width: ${size}px hint with no min/max pin).
        tableEl.style.removeProperty(`--col-${header.column.id}-w`);
        tableEl.style.removeProperty(`--col-${header.column.id}-mw`);
        tableEl.style.removeProperty(`--col-${header.column.id}-Mw`);
      }
      table.setColumnSizing((prev) => {
        const next = { ...prev };
        delete next[header.column.id];
        return next;
      });
    },
    [header.column.id, table],
  );

  return (
    <>
      <div
        role="separator"
        tabIndex={-1}
        aria-orientation="vertical"
        aria-label="Resize column"
        onPointerDown={onPointerDown}
        onDoubleClick={onDoubleClick}
        onClick={(e) => e.stopPropagation()}
        css={styles.resizeHandle}
        className={isResizing ? 'resizing' : undefined}
      />
      {hint !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div
              css={styles.resizeGuideline}
              style={{
                left: hint.edgeX,
                top: hint.tableTop,
                height: Math.max(0, hint.tableBottom - hint.tableTop),
              }}
            />
            <div
              css={styles.resizeHint}
              style={{ left: hint.hintX + 14, top: hint.hintY + 14 }}
            >
              {hint.width}px
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

interface TableBodyProps<T> {
  dataRef: MutableRefObject<T[]>;
  table: ReactTable<T>;
  cols: Column<T>[];
  rows: Row<T>[];
  expandedRowRender?: (row: Row<T>) => ReactNode;
  expandedRowSpansFull?: boolean;
  afterEachRowRender?: (row: Row<T>) => ReactNode;
  maxNumRows: number;
  loading: boolean | undefined;
  isError: boolean;
  errorState: ReactNode | undefined;
  isEmpty: boolean;
  emptyState: ReactNode | undefined;
  topPinnedRowIds?: string[];
  bottomPinnedRowIds?: string[];
}

export function TableBody<T>({
  dataRef,
  table,
  cols,
  rows,
  expandedRowRender,
  expandedRowSpansFull,
  afterEachRowRender,
  maxNumRows,
  loading,
  isError,
  errorState,
  isEmpty,
  emptyState,
  topPinnedRowIds,
  bottomPinnedRowIds,
}: TableBodyProps<T>) {
  if (isError) {
    return <ErrorState errorState={errorState} />;
  }

  if (loading) {
    return (
      <SkeletonRows
        cols={cols}
        numRows={maxNumRows}
      />
    );
  }

  if (isEmpty) {
    return <EmptyState emptyState={emptyState} />;
  }

  return (
    <Rows<T>
      dataRef={dataRef}
      table={table}
      rows={rows}
      expandedRowRender={expandedRowRender}
      expandedRowSpansFull={expandedRowSpansFull}
      afterEachRowRender={afterEachRowRender}
      topPinnedRowIds={topPinnedRowIds}
      bottomPinnedRowIds={bottomPinnedRowIds}
    />
  );
}

interface RowsProps<T> {
  dataRef: MutableRefObject<T[]>;
  table: ReactTable<T>;
  rows: Row<T>[];
  expandedRowRender?: (row: Row<T>) => ReactNode;
  expandedRowSpansFull?: boolean;
  afterEachRowRender?: (row: Row<T>) => ReactNode;
  topPinnedRowIds?: string[];
  bottomPinnedRowIds?: string[];
}

export function Rows<T>({
  dataRef,
  table,
  rows,
  expandedRowRender,
  expandedRowSpansFull,
  afterEachRowRender,
  topPinnedRowIds,
  bottomPinnedRowIds,
}: RowsProps<T>) {
  return (
    <>
      {rows.map((row) => (
        <Fragment key={row.id}>
          {topPinnedRowIds?.includes(row.id) ? (
            <StickyTopTr
              dataRef={dataRef}
              table={table}
              row={row}
            />
          ) : bottomPinnedRowIds?.includes(row.id) ? (
            <StickyBottomTr
              dataRef={dataRef}
              table={table}
              row={row}
            />
          ) : (
            <Tr
              dataRef={dataRef}
              table={table}
              row={row}
            />
          )}
          {row.getIsExpanded() && expandedRowRender && (
            <ExpandedTr
              row={row}
              expandedRowRender={expandedRowRender}
              expandedRowSpansFull={expandedRowSpansFull}
            />
          )}
          {afterEachRowRender?.(row)}
        </Fragment>
      ))}
    </>
  );
}

interface TableFooterRowsProps<T> {
  dataRef: MutableRefObject<T[]>;
  table: ReactTable<T>;
  rows: Row<T>[];
}

export function TableFooterRows<T>({ dataRef, table, rows }: TableFooterRowsProps<T>) {
  return (
    <>
      {rows.map((row) => (
        <StickyBottomTr
          key={row.id}
          dataRef={dataRef}
          table={table}
          row={row}
        />
      ))}
    </>
  );
}

interface TrProps<T> {
  dataRef: MutableRefObject<T[]>;
  table: ReactTable<T>;
  row: Row<T>;
}

export function Tr<T>({ dataRef, table, row }: TrProps<T>) {
  const trRef = useRef<HTMLTableRowElement>(null);
  const getCommonPinningStylesCb = useCallback(
    (column: Column<T>) => getCommonPinningStyles(trRef, table, column),
    [dataRef, table],
  );

  useRowResizeRedraw(trRef, table);

  return (
    <tr
      ref={trRef}
      css={styles.tr}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          data-col-id={cell.column.id}
          css={[
            styles.td,
            areRowsVerticalAlignTop(cell.getContext().table.options.meta) &&
              getCellAlignItemsOption(cell.getContext().column.columnDef.meta) !== 'center' &&
              styles.tdVerticalAlignTop,
            ...getPinnedColumnClasses(cell.column),
          ]}
          style={{
            ...(cell.column.getSize() === 0 ? { width: '1px' } : {}),
            ...getCommonPinningStylesCb(cell.column),
            ...(isGrowColumn(cell.column.columnDef) ? { width: '100%' } : {}),
          }}
        >
          <div
            css={[
              styles.cell,
              isRightAligned(cell.getContext().column.columnDef) && styles.cellRight,
              areRowsVerticalAlignTop(cell.getContext().table.options.meta) &&
                getCellAlignItemsOption(cell.getContext().column.columnDef.meta) !== 'center' &&
                styles.cellVerticalAlignTop,
            ]}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
          <div css={[...(cell.column.getIsPinned() ? [styles.tdLeftPinnedShadow] : [])]} />
        </td>
      ))}
    </tr>
  );
}

export function StickyBottomTr<T>({ dataRef, table, row }: TrProps<T>) {
  const trRef = useRef<HTMLTableRowElement>(null);
  const getCommonPinningStylesCb = useCallback(
    (column: Column<T>) => getCommonPinningStyles(trRef, table, column),
    [dataRef, table],
  );

  useRowResizeRedraw(trRef, table);

  return (
    <tr
      ref={trRef}
      css={styles.tr}
    >
      {row.getVisibleCells().map((cell, index) => (
        <td
          key={cell.id}
          data-col-id={cell.column.id}
          css={[
            styles.td,
            styles.tdStickyBottom,
            areRowsVerticalAlignTop(cell.getContext().table.options.meta) &&
              getCellAlignItemsOption(cell.getContext().column.columnDef.meta) !== 'center' &&
              styles.tdVerticalAlignTop,
            ...getPinnedColumnClasses(cell.column),
          ]}
          style={{
            ...(cell.column.getSize() === 0 ? { width: '1px' } : {}),
            ...getCommonPinningStylesCb(cell.column),
            ...(isGrowColumn(cell.column.columnDef) ? { width: '100%' } : {}),
          }}
        >
          <div
            css={[
              styles.cell,
              isRightAligned(cell.getContext().column.columnDef) && styles.cellRight,
              areRowsVerticalAlignTop(cell.getContext().table.options.meta) &&
                getCellAlignItemsOption(cell.getContext().column.columnDef.meta) !== 'center' &&
                styles.cellVerticalAlignTop,
            ]}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
          <div css={styles.tdStickyBottomShadow} />
          <div
            css={[...(cell.column.getIsPinned() ? [styles.tdStickyBottomLeftPinnedShadow] : [])]}
          />
        </td>
      ))}
    </tr>
  );
}

// Mirror of StickyBottomTr for a row pinned to the TOP of the scroll body
// (e.g. a grand-totals summary row). Because the sticky `<thead>` already
// owns `top: 0`, this row parks just below it using a dynamically-measured
// offset equal to the current header height, so the two never overlap.
export function StickyTopTr<T>({ dataRef, table, row }: TrProps<T>) {
  const trRef = useRef<HTMLTableRowElement>(null);
  const getCommonPinningStylesCb = useCallback(
    (column: Column<T>) => getCommonPinningStyles(trRef, table, column),
    [dataRef, table],
  );

  useRowResizeRedraw(trRef, table);

  // Distance from the top of the scroll container to park this row at — the
  // rendered height of the sticky header. Re-measured whenever the header (or
  // this row) resizes so the offset stays correct across responsive changes.
  const [topOffset, setTopOffset] = useState(0);
  useEffect(() => {
    const thead = trRef.current?.closest('table')?.querySelector('thead');
    if (!thead) return;

    // Ignore sub-pixel height noise (fractional-DPR displays report slightly
    // different thead heights across observer ticks) so this row doesn't churn
    // state on every notification.
    const update = () => {
      const height = thead.getBoundingClientRect().height;
      setTopOffset((prev) => (Math.abs(prev - height) < SUBPIXEL_EPSILON ? prev : height));
    };
    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(thead);
    if (trRef.current) {
      resizeObserver.observe(trRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <tr
      ref={trRef}
      css={styles.tr}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          data-col-id={cell.column.id}
          css={[
            styles.td,
            styles.tdStickyTop,
            areRowsVerticalAlignTop(cell.getContext().table.options.meta) &&
              getCellAlignItemsOption(cell.getContext().column.columnDef.meta) !== 'center' &&
              styles.tdVerticalAlignTop,
            ...getPinnedColumnClasses(cell.column),
            // Applied last so it wins the z-index: a left-pinned cell in this
            // row is sticky on both axes and must sit above its own center
            // cells during horizontal scroll.
            ...(cell.column.getIsPinned() ? [styles.tdStickyTopPinned] : []),
          ]}
          style={{
            top: topOffset,
            ...(cell.column.getSize() === 0 ? { width: '1px' } : {}),
            ...getCommonPinningStylesCb(cell.column),
            ...(isGrowColumn(cell.column.columnDef) ? { width: '100%' } : {}),
          }}
        >
          <div
            css={[
              styles.cell,
              isRightAligned(cell.getContext().column.columnDef) && styles.cellRight,
              areRowsVerticalAlignTop(cell.getContext().table.options.meta) &&
                getCellAlignItemsOption(cell.getContext().column.columnDef.meta) !== 'center' &&
                styles.cellVerticalAlignTop,
            ]}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
          <div css={styles.tdStickyTopShadow} />
          <div css={[...(cell.column.getIsPinned() ? [styles.tdStickyTopLeftPinnedShadow] : [])]} />
        </td>
      ))}
    </tr>
  );
}

interface ExpandedTrProps<T> {
  row: Row<T>;
  expandedRowRender: (row: Row<T>) => ReactNode;
  expandedRowSpansFull?: boolean;
}

function ExpandedTr<T>({
  row,
  expandedRowRender,
  expandedRowSpansFull = true,
}: ExpandedTrProps<T>) {
  if (!expandedRowSpansFull) {
    return expandedRowRender(row);
  }

  return (
    <tr css={styles.tr}>
      <td
        colSpan={row.getVisibleCells().length}
        css={styles.td}
      >
        {expandedRowRender(row)}
      </td>
    </tr>
  );
}

interface EdgeStateRowProps {
  children: ReactNode;
}

function EdgeStateRow({ children }: EdgeStateRowProps) {
  return (
    <tr css={styles.tdEdgeState}>
      <td
        colSpan={100}
        style={{
          padding: '16px',
          textAlign: 'center',
          height: 64,
          color: grey[400],
        }}
      >
        {children}
      </td>
    </tr>
  );
}

interface ErrorStateProps {
  errorState?: ReactNode;
}

export function ErrorState({ errorState }: ErrorStateProps) {
  return (
    <EdgeStateRow>
      {errorState || (
        <Typography
          variant="h6"
          component="h3"
        >
          Error
        </Typography>
      )}
    </EdgeStateRow>
  );
}

interface EmptyStateProps {
  emptyState?: ReactNode;
}

export function EmptyState({ emptyState }: EmptyStateProps) {
  return (
    <EdgeStateRow>
      {emptyState || (
        <Typography
          variant="h6"
          component="h3"
        >
          No data
        </Typography>
      )}
    </EdgeStateRow>
  );
}

interface SkeletonRowsProps {
  cols: Column<any, unknown>[];
  numRows?: number;
}

export function SkeletonRows({ cols, numRows }: SkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: numRows ? numRows : 5 }).map((_, i) => (
        <tr
          css={styles.tr}
          key={i}
        >
          {cols.map((col, j) => (
            <td
              key={`${col.id}-${j}`}
              css={styles.td}
              style={{
                ...(col.getSize() === 0 ? { width: '1px' } : {}),
                ...(isGrowColumn(col.columnDef) ? { width: '100%' } : {}),
              }}
            >
              <div css={styles.cell}>
                <Skeleton sx={{ width: '100%' }} />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

interface SortingButtonProps<TData, TValue> {
  head: Header<TData, TValue>;
  onOrderByChange?: (orderBy: ColumnSort | undefined) => void;
}

function SortingButton<TData, TValue>({
  head,
  onOrderByChange,
}: SortingButtonProps<TData, TValue>) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onClick = useCallback(
    (e: any) => {
      const isSorted = head.column.getIsSorted();
      onOrderByChange?.(
        (isSorted as string) === 'asc'
          ? undefined
          : {
              id: head.column.id,
              desc: (isSorted as string) !== 'desc',
            },
      );

      head.column.getToggleSortingHandler()?.(e);
    },

    [head.column],
  );

  return (
    <IconButton
      onClick={onClick}
      aria-label="sort"
      size="small"
      css={[
        !head.column.getIsSorted() && styles.isNotSorted,
        head.column.getIsSorted() && styles.isSorted,
      ]}
      className={head.column.getCanSort() ? 'sortButton' : ''}
    >
      {
        {
          none: (
            <ArrowDownward
              fontSize="small"
              css={styles.sortButtonIcon}
            />
          ),
          desc: (
            <ArrowDownward
              fontSize="small"
              css={styles.sortButtonIcon}
            />
          ),
          asc: (
            <ArrowUpward
              fontSize="small"
              css={styles.sortButtonIcon}
            />
          ),
        }[(head.column.getIsSorted() as string) || 'none']
      }
    </IconButton>
  );
}

function isRightAligned<TData, TValue>(columnDef: ColumnDef<TData, TValue>) {
  if (columnDef.meta && Object.prototype.hasOwnProperty.call(columnDef.meta, 'align')) {
    return (columnDef.meta as { align: 'right' }).align === 'right';
  }

  return false;
}

function areRowsVerticalAlignTop(tableMeta: any) {
  if (
    tableMeta &&
    typeof tableMeta === 'object' &&
    tableMeta.hasOwnProperty('rowsAlignedTopVertically')
  ) {
    return Boolean(tableMeta.rowsAlignedTopVertically);
  }

  return false;
}

function getCellAlignItemsOption(cellMeta: any) {
  if (cellMeta && typeof cellMeta === 'object' && cellMeta.hasOwnProperty('alignItems')) {
    return cellMeta.alignItems;
  }

  return undefined;
}

function isGrowColumn(columnDef: any) {
  if (columnDef.meta && typeof columnDef.meta === 'object' && columnDef.meta.grow === true) {
    return true;
  }
  return false;
}

const PAGE_SIZES = [10, 25, 50, 100];

export interface TableFooterProps {
  showPagination?: boolean;
  maxPageSize?: number;
  onNextPageClick?: () => void;
  canGetNextPage?: boolean;
  onPrevPageClick?: () => void;
  canGetPrevPage?: boolean;
  showPageSizeSelect?: boolean;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  rowsCount?: number;
  sx?: SxProps;
}

export function TableFooter({
  showPagination,
  onNextPageClick,
  canGetNextPage,
  onPrevPageClick,
  canGetPrevPage,
  showPageSizeSelect,
  maxPageSize = 100,
  pageSize = 10,
  onPageSizeChange,
  rowsCount,
  sx,
}: TableFooterProps) {
  const { numberFormatLocale } = useLocalization();
  return (
    <Stack
      direction="row"
      // Pagination is interactive-only chrome — print styles hide it (see app print.css)
      data-print-hidden
      sx={{
        gap: 2,
        alignItems: 'center',
        ...sx,
        pl: 2,
        mt: 2,
      }}
    >
      {rowsCount && !Number.isNaN(rowsCount) && (
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Text size="small">
            {formatNumber(rowsCount, numberFormatLocale)} {pluralize(rowsCount, 'result')}
          </Text>
        </Box>
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'end',
          flexGrow: 1,
          gap: 2,
        }}
      >
        {showPageSizeSelect && (
          <FormControl
            size="small"
            variant="filled"
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Text size="small">Rows per page</Text>
              <Select
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange?.(Number(e.target.value));
                }}
              >
                {PAGE_SIZES.filter((size) => size <= maxPageSize).map((size) => (
                  <MenuItem
                    key={size}
                    value={size}
                  >
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </FormControl>
        )}
        {showPagination && (canGetPrevPage || canGetNextPage) && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <IconButton
              onClick={onPrevPageClick}
              disabled={!canGetPrevPage}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={onNextPageClick}
              disabled={!canGetNextPage}
              aria-label="Next page"
            >
              <ChevronRight />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

function getCommonPinningStyles<T>(
  trRef: RefObject<Element | null>,
  table: ReactTable<T>,
  column: Column<T>,
): CSSProperties {
  const isPinned = column.getIsPinned();
  const id = column.id;
  const size = column.getSize();
  const sizingState = table.getState().columnSizing;
  const isUserResized = sizingState[id] !== undefined;

  // Three CSS custom properties drive each cell's width during a resize drag,
  // written directly to the <table> element by ResizeHandle so the column
  // visually tracks the cursor without firing a React re-render per
  // pointermove. We keep `table-layout: auto` (so unresized columns auto-fit
  // their content as before), and pin resized columns via `min-width` +
  // `max-width` — the browser respects these strictly even under auto layout,
  // overriding the content-min-content floor that otherwise prevents columns
  // from shrinking below their longest text.
  //
  // - --col-{id}-w  → width
  // - --col-{id}-mw → min-width
  // - --col-{id}-Mw → max-width
  //
  // Fallbacks: pixel values when the column has been user-resized (so it
  // stays pinned across re-renders), `auto`/`none` otherwise (so the column
  // continues to auto-fit content).
  const widthValue = `var(--col-${id}-w, ${size}px)`;
  const minWidthFallback = isUserResized ? `${size}px` : 'auto';
  const maxWidthFallback = isUserResized ? `${size}px` : 'none';
  const styleOut: CSSProperties = {
    // CSSProperties' Width type accepts arbitrary strings via `string & {}`,
    // but TS sometimes narrows it. Casting via `unknown` is a runtime no-op
    // and keeps the value as the CSS expression React will write to the DOM.
    width: widthValue as unknown as CSSProperties['width'],
    minWidth: `var(--col-${id}-mw, ${minWidthFallback})` as unknown as CSSProperties['minWidth'],
    maxWidth: `var(--col-${id}-Mw, ${maxWidthFallback})` as unknown as CSSProperties['maxWidth'],
  };

  if (isPinned === 'right') {
    styleOut.right = `${column.getAfter('right')}px`;
    return styleOut;
  }

  if (isPinned !== 'left') {
    return styleOut;
  }

  // Cumulative width of preceding left-pinned columns. We iterate
  // VISIBLE leaf columns rather than all leaf columns so the loop
  // index matches the `nth-of-type` position in the rendered DOM —
  // hidden columns don't render a <td>, so an all-columns index would
  // overshoot and grab the wrong neighbor's width. For columns that
  // have been explicitly resized (and so live in `columnSizing`),
  // trust that value — it stays correct across the React render that
  // triggered the resize, even before layout commits. For auto-sized
  // columns we fall back to the rendered DOM width because
  // `column.getSize()` returns the auto-fit hint (1px) rather than the
  // visual width.
  let leftOffset = 0;
  const visibleCols = table.getVisibleLeafColumns();
  const myVisibleIndex = visibleCols.indexOf(column);
  if (myVisibleIndex > 0) {
    for (let i = 0; i < myVisibleIndex; i++) {
      const prevCol = visibleCols[i];
      if (prevCol.getIsPinned() !== 'left') continue;

      const explicitSize = sizingState[prevCol.id];
      if (typeof explicitSize === 'number') {
        leftOffset += explicitSize;
        continue;
      }

      const prevCell = trRef.current?.querySelector(
        `td:nth-of-type(${i + 1}), th:nth-of-type(${i + 1})`,
      );
      if (prevCell) leftOffset += prevCell.clientWidth;
    }
  }

  styleOut.left = `var(--col-${id}-l, ${leftOffset}px)`;
  return styleOut;
}

function getPinnedColumnClasses<T>(column: Column<T>) {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  if (!isPinned) {
    return [];
  }

  let classes = [styles.tdPinned];
  classes = classes.concat(
    isLastLeftPinnedColumn
      ? styles.tdLeftPinned
      : isFirstRightPinnedColumn
        ? styles.tdRightPinned
        : [],
  );

  return classes;
}

function getPinnedColumnThClasses<T>(column: Column<T>) {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  if (!isPinned) {
    return [];
  }

  let classes = [styles.thPinned];
  classes = classes.concat(
    isLastLeftPinnedColumn
      ? styles.thLeftPinned
      : isFirstRightPinnedColumn
        ? styles.thRightPinned
        : [],
  );

  return classes;
}
